import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
//@ts-ignore
import { ToastNotification } from '@carbon/react';

interface ToastContextType {
  showSuccessToast: (message: string) => void;
  showErrorToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastState {
  show: boolean;
  message: string;
}

// Глобальное состояние тостов, вынесенное из React дерева
let globalToastState = {
  successToast: { show: false, message: '' },
  errorToast: { show: false, message: '' }
};

let toastListeners: (() => void)[] = [];

const notifyToastListeners = () => {
  toastListeners.forEach(listener => listener());
};

// Изолированный компонент для рендеринга тостов
const ToastRenderer: React.FC = React.memo(() => {
  const [successToast, setSuccessToast] = useState<ToastState>(globalToastState.successToast);
  const [errorToast, setErrorToast] = useState<ToastState>(globalToastState.errorToast);

  useEffect(() => {
    const updateToasts = () => {
      setSuccessToast({ ...globalToastState.successToast });
      setErrorToast({ ...globalToastState.errorToast });
    };

    toastListeners.push(updateToasts);

    return () => {
      toastListeners = toastListeners.filter(listener => listener !== updateToasts);
    };
  }, []);

  const closeSuccessToast = useCallback(() => {
    globalToastState.successToast = { show: false, message: '' };
    notifyToastListeners();
  }, []);

  const closeErrorToast = useCallback(() => {
    globalToastState.errorToast = { show: false, message: '' };
    notifyToastListeners();
  }, []);

  return (
    <>
      {successToast.show && (
        <div style={{
          position: 'fixed',
          top: 60,
          right: 60,
          zIndex: 9999999,
          pointerEvents: 'auto'
        }}>
          <ToastNotification
            kind="success"
            hideCloseButton={false}
            lowContrast={false}
            title="Внимание"
            subtitle={successToast.message}
            timeout={4000}
            onClose={closeSuccessToast}
          />
        </div>
      )}
      
      {errorToast.show && (
        <div style={{
          position: 'fixed',
          top: 60,
          right: 60,
          zIndex: 9999999,
          pointerEvents: 'auto'
        }}>
          <ToastNotification
            kind="error"
            hideCloseButton={false}
            lowContrast={false}
            title="Внимание"
            subtitle={errorToast.message}
            timeout={4000}
            onClose={closeErrorToast}
          />
        </div>
      )}
    </>
  );
});

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const showSuccessToast = useCallback((message: string) => {
    globalToastState.successToast = { show: true, message };
    notifyToastListeners();
  }, []);

  const showErrorToast = useCallback((message: string) => {
    globalToastState.errorToast = { show: true, message };
    notifyToastListeners();
  }, []);

  // Мемоизируем контекстное значение чтобы избежать лишних перерендеров
  const contextValue = React.useMemo(() => ({
    showSuccessToast,
    showErrorToast
  }), [showSuccessToast, showErrorToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastRenderer />
    </ToastContext.Provider>
  );
}; 